// Pure helpers of the task drawer and the approvals queue (no DOM): labels, timings, timeline,
// tools, usage, callback status, available actions, approval/rejection bodies, queue ordering.
// Unit-tested in test/ui-tasks-model.test.js.
import { t } from '../../i18n/fr.js';
import { connectorLabel, formatNumber, formatCost, formatDateTime, formatDuration, relativeTime } from '../../format.js';
import { matchConnector, isLibraryConnector } from '../connectorlines.js';
import './strings.js';

/** States in which an agent process is running. */
export const RUNNING_STATES = new Set(['running', 'executing']);
/** States a task can be cancelled from (SPEC §12). */
export const CANCELLABLE_STATES = new Set(['scheduled', 'queued', 'running', 'executing']);
/** States a task can be retried from (SPEC §12). */
export const RETRYABLE_STATES = new Set(['failed', 'cancelled', 'rejected']);
/** Final states. */
export const FINAL_STATES = new Set(['done', 'failed', 'rejected', 'cancelled']);
/** Maximum feedback length sent with a rejection (the prompt caps it at 4 000). */
export const MAX_FEEDBACK = 4000;
/** A lesson is cut to this length by the server. */
export const MAX_LESSON = 300;

/**
 * Milliseconds from a number, an ISO string or a Date (null when missing or invalid).
 * @param {unknown} v
 * @returns {number|null}
 */
export function toMs(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (v instanceof Date) return Number.isFinite(v.getTime()) ? v.getTime() : null;
  const s = String(v);
  const n = /^\d+$/.test(s) ? Number(s) : Date.parse(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * The timestamps of a task and its work duration.
 * duration = startedAt → finishedAt (or → waitingSince while awaiting approval, → now while running).
 * @param {object} task
 * @param {number} [now]
 * @returns {{created: number|null, started: number|null, finished: number|null, due: number|null,
 *   waitingSince: number|null, durationMs: number|null, waitingMs: number|null, live: boolean}}
 */
export function taskTimes(task, now = Date.now()) {
  const created = toMs(task?.createdAt);
  const started = toMs(task?.startedAt);
  const finished = toMs(task?.finishedAt);
  const due = toMs(task?.dueAt);
  const waitingSince = toMs(task?.waitingSince);
  const live = RUNNING_STATES.has(task?.state);
  let end = null;
  if (live) end = now;
  else if (finished) end = finished;
  else if (task?.state === 'awaiting_approval' && waitingSince) end = waitingSince;
  const durationMs = started && end && end >= started ? end - started : null;
  const waitingMs = task?.state === 'awaiting_approval' && waitingSince ? Math.max(0, now - waitingSince) : null;
  return { created, started, finished, due, waitingSince, durationMs, waitingMs, live };
}

/**
 * Where a task came from, as an icon name and a French label.
 * @param {object} task
 * @param {{meId?: string|null, routineName?: string|null, triggerName?: string|null}} [opts]
 * @returns {{kind: string, icon: string, text: string}}
 */
export function sourceInfo(task, { meId = null, routineName = null, triggerName = null } = {}) {
  const s = task?.source && typeof task.source === 'object' ? task.source : {};
  const kind = ['user', 'routine', 'trigger', 'chat', 'calendar', 'delegation', 'watch'].includes(s.kind) ? s.kind : 'user';
  const mine = !!(meId && s.userId && s.userId === meId);
  const who = typeof s.userName === 'string' && s.userName.trim() ? s.userName.trim() : '';
  switch (kind) {
    case 'routine': {
      const name = routineName || s.routineTitle || s.routineName || s.title || s.routineId || t('td.src.unnamedRoutine');
      return { kind, icon: 'repeat', text: t('taskdrawer.source.routine', { name }) };
    }
    case 'trigger': {
      const name = triggerName || s.triggerName || s.name || s.triggerId || t('td.src.unnamedTrigger');
      return { kind, icon: 'bolt', text: t('taskdrawer.source.trigger', { name }) };
    }
    case 'delegation': {
      // A task confiée by another agent (SPEC-EQUIPE §1): « Demandée par {agent} », linked to the parent.
      const name = s.byAgentName || task.parent?.agentName || s.byAgentId || t('td.src.colleague');
      return { kind, icon: 'arrowRight', text: t('td.src.delegation', { name }), parentId: typeof s.parentTaskId === 'string' ? s.parentTaskId : null };
    }
    case 'watch':
      // Started by the Studio's « contrôle de production » about an ad (linked to its page).
      return { kind, icon: 'film', text: t('td.src.watch'), itemId: typeof s.itemId === 'string' ? s.itemId : null };
    case 'chat':
      return { kind, icon: 'send', text: mine ? t('td.src.chatYou') : who ? t('td.src.chatBy', { name: who }) : t('taskdrawer.source.chat') };
    case 'calendar':
      return { kind, icon: 'calendar', text: mine ? t('td.src.calendarYou') : who ? t('td.src.calendarBy', { name: who }) : t('taskdrawer.source.calendar') };
    default:
      return { kind, icon: 'user', text: mine ? t('td.src.you') : who ? t('taskdrawer.source.user', { name: who }) : t('td.src.someone') };
  }
}

const TIMELINE_ICONS = {
  created: 'plus', routed: 'arrowRight', queued: 'clock', scheduled: 'calendar', started: 'bolt', tool: 'tool',
  tools: 'tool', draft: 'inbox', approved: 'check', rejected: 'close', executing: 'bolt', done: 'check',
  failed: 'alert', error: 'alert', cancelled: 'close', retried: 'refresh', retry: 'refresh', revision: 'refresh',
  callback: 'send', warning: 'alert', warn: 'alert', late: 'clock', files: 'layers', edited: 'check',
  delegated: 'arrowRight', idle: 'check', blocked: 'alert', personal: 'user', todo: 'alert', settled: 'check',
  ignored: 'eyeOff', unignored: 'eye', superseded: 'check',
};
const TIMELINE_TONES = {
  approved: 'success', done: 'success', failed: 'danger', error: 'danger', rejected: 'amber', draft: 'amber',
  warning: 'amber', warn: 'amber', late: 'amber', cancelled: 'muted', blocked: 'amber', todo: 'amber', settled: 'success',
  ignored: 'muted', superseded: 'success',
};

/** Icon name of a timeline entry kind. */
export function timelineIcon(kind) {
  return TIMELINE_ICONS[kind] || 'dots';
}

/** Tone ('success' | 'danger' | 'amber' | 'muted' | '') of a timeline entry kind. */
export function timelineTone(kind) {
  return TIMELINE_TONES[kind] || '';
}

/**
 * Timeline entries from task.log, in order. Runs of `collapse` or more consecutive 'tool' entries
 * become one {kind: 'tools', at, until, items} group. Junk entries are skipped.
 * @param {Array<{at, kind, text}>} log
 * @param {{collapse?: number}} [opts]
 */
export function buildTimeline(log, { collapse = 3 } = {}) {
  const out = [];
  let run = [];
  const flush = () => {
    if (run.length >= collapse) out.push({ kind: 'tools', at: run[0].at, until: run[run.length - 1].at, items: run });
    else out.push(...run);
    run = [];
  };
  for (const e of Array.isArray(log) ? log : []) {
    if (!e || typeof e !== 'object') continue;
    const entry = { at: e.at ?? null, kind: typeof e.kind === 'string' && e.kind ? e.kind : 'info', text: e.text == null ? '' : String(e.text) };
    if (entry.kind === 'tool') run.push(entry);
    else { flush(); out.push(entry); }
  }
  flush();
  return out;
}

const BUILTIN_TOOLS = {
  WebSearch: 'td.tool.webSearch', WebFetch: 'td.tool.webFetch', web_search: 'td.tool.webSearch',
  Write: 'td.tool.files', Edit: 'td.tool.files', Read: 'td.tool.files', Glob: 'td.tool.files', file_change: 'td.tool.files',
  image_gen: 'td.tool.image', imagegen: 'td.tool.image', Bash: 'td.tool.shell', command_execution: 'td.tool.shell',
};

/**
 * Groups the tools a run used into chips: one per connector (MCP tools; a connector of the office's
 * library by its exact key only, a single neutral one for those no longer listed), one per
 * built-in family.
 * @param {string[]} toolsUsed full tool names (mcp__server__tool, WebSearch, codex-server/tool…)
 * @param {Array<{key: string, name?: string}>} [connectors]
 * @returns {Array<{label: string, key: string|null, tools: string[], count: number}>}
 */
export function toolChips(toolsUsed, connectors = []) {
  const groups = new Map();
  const add = (id, label, key, tool) => {
    if (!groups.has(id)) groups.set(id, { label, key, tools: [], count: 0 });
    const g = groups.get(id);
    g.count += 1;
    if (tool && !g.tools.includes(tool)) g.tools.push(tool);
  };
  for (const raw of Array.isArray(toolsUsed) ? toolsUsed : []) {
    if (typeof raw !== 'string' || !raw.trim()) continue;
    const name = raw.trim();
    const mcp = /^mcp__(.+?)__(.+)$/.exec(name) || /^mcp__(.+)$/.exec(name);
    if (mcp) {
      const key = mcp[1];
      // A library connector: its own chip while it is listed (by its exact key: its label may be
      // a detected connector's name), else one neutral chip (removed from the library since).
      if (isLibraryConnector(key)) {
        const listed = connectors.find((c) => c.key === key);
        if (listed) add(`mcp:${key}`, connectorLabel(listed.label || listed.name || key), key, mcp[2] || '');
        else add('library', t('td.tool.library'), null, mcp[2] || '');
        continue;
      }
      const known = connectors.find((c) => c.key === key) || connectors.find((c) => c.key === matchConnector(key, connectors));
      const label = connectorLabel(known?.name || key.replace(/^claude_ai_/i, '').replace(/_/g, ' '));
      add(`mcp:${known?.key || key}`, label, known?.key || key, mcp[2] || '');
      continue;
    }
    const base = name.replace(/\(.*$/, '');
    if (BUILTIN_TOOLS[base]) {
      add(`builtin:${BUILTIN_TOOLS[base]}`, t(BUILTIN_TOOLS[base]), null, base);
      continue;
    }
    const slash = /^([^/\s]+)\/(.+)$/.exec(name); // codex: server/tool
    if (slash) {
      const key = slash[1];
      const known = connectors.find((c) => c.key === key || c.key === `codex-${key}` || c.id === key);
      add(`mcp:${known?.key || key}`, connectorLabel(known?.name || key.replace(/^codex-/, '')), known?.key || key, slash[2]);
      continue;
    }
    add(`other:${name}`, name.length > 40 ? `${name.slice(0, 39)}…` : name, null, '');
  }
  return [...groups.values()];
}

/**
 * Display rows of a run's usage (admin only). Missing values are skipped.
 * @param {{inputTokens?, outputTokens?, cacheReadTokens?, cacheWriteTokens?, costUsd?}} usage
 * @returns {Array<{key: string, label: string, value: string}>}
 */
export function usageRows(usage) {
  if (!usage || typeof usage !== 'object') return [];
  const rows = [];
  const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  const push = (key, labelKey, v, fmt) => { if (v !== null) rows.push({ key, label: t(labelKey), value: fmt(v) }); };
  push('input', 'td.usage.input', num(usage.inputTokens), (v) => formatNumber(v));
  push('output', 'td.usage.output', num(usage.outputTokens), (v) => formatNumber(v));
  if (num(usage.cacheReadTokens)) push('cacheRead', 'td.usage.cacheRead', num(usage.cacheReadTokens), (v) => formatNumber(v));
  if (num(usage.cacheWriteTokens)) push('cacheWrite', 'td.usage.cacheWrite', num(usage.cacheWriteTokens), (v) => formatNumber(v));
  push('cost', 'td.usage.cost', num(usage.costUsd), (v) => formatCost(v));
  return rows;
}

/**
 * The callback status of a trigger task, for display.
 * @param {{state, attempts, lastError, nextAt, sentAt}|null} cb
 * @param {number} [now]
 * @returns {{tone: string, badge: string, text: string, error: string}|null}
 */
export function callbackInfo(cb, now = Date.now()) {
  if (!cb || typeof cb !== 'object' || !cb.state) return null;
  const attempts = Math.max(0, Number(cb.attempts) || 0);
  const error = cb.lastError ? t('td.callback.lastError', { error: String(cb.lastError).slice(0, 300) }) : '';
  switch (cb.state) {
    case 'sent':
      return { tone: 'success', badge: t('td.callback.sent'), text: t('td.callback.sentText', { when: formatDateTime(cb.sentAt, now) }), error: '' };
    case 'failed':
      return { tone: 'danger', badge: t('td.callback.failed'), text: t('td.callback.failedText', { n: attempts }), error };
    default: {
      const next = toMs(cb.nextAt);
      const text = attempts > 0 && next ? t('td.callback.retryText', { n: attempts, when: relativeTime(next, now) }) : t('td.callback.pendingText');
      return { tone: 'amber', badge: t('td.callback.pending'), text, error };
    }
  }
}

/**
 * Which actions the drawer offers for a task.
 * @param {object} task
 * @param {{canOperate?: boolean, canManage?: boolean}} perms
 * @returns {{approve: boolean, reject: boolean, cancel: boolean, retry: boolean, delete: boolean,
 *   ignore: boolean, unignore: boolean}}
 */
export function taskActions(task, { canOperate = false, canManage = false } = {}) {
  const state = task?.state;
  const waiting = state === 'awaiting_approval';
  const ignored = Boolean(task?.ignored);
  return {
    approve: canOperate && waiting,
    reject: canOperate && waiting,
    cancel: canOperate && CANCELLABLE_STATES.has(state),
    // A task stopped by an external cause (« BLOQUÉ : … ») is redone once the cause is fixed.
    retry: canOperate && (RETRYABLE_STATES.has(state) || (state === 'done' && Boolean(task?.blocked))),
    // A failure, a blocking or a decision « À RÉGLER » nobody needs to act on: no error any more
    // (a draft is set aside from the board).
    ignore: canOperate && !ignored && (state === 'failed' || (state === 'done' && (Boolean(task?.blocked) || (Boolean(task?.todo) && !task?.settled)))),
    unignore: canOperate && ignored,
    delete: canManage && !!state && !RUNNING_STATES.has(state),
  };
}

/**
 * The runs of a task (its `attempts` history) as display lines, oldest first: number, outcome,
 * end time and the error or the blocking reason. Empty when the task ran once at most and does
 * not run again.
 * @param {object} task TaskDetail
 * @returns {Array<{n: number, state: string, label: string, at: number|null, text: string}>}
 */
export function attemptLines(task) {
  const list = (Array.isArray(task?.attempts) ? task.attempts : []).filter((a) => a && typeof a === 'object');
  const again = !FINAL_STATES.has(task?.state) && task?.state !== 'awaiting_approval';
  if (list.length < 2 && !(list.length && again)) return [];
  return list.map((a) => {
    const state = ['failed', 'blocked', 'idle', 'done', 'cancelled'].includes(a.state) ? a.state : 'done';
    const text = state === 'failed' ? String(a.error || '') : state === 'blocked' ? String(a.blockedReason || '') : '';
    return { n: Number(a.n) || 0, state, label: t(`td.attempt.${state}`), at: toMs(a.finishedAt) ?? toMs(a.startedAt), text };
  });
}

/**
 * Checks the reject form: feedback is required when the agent must redo the work or remember a rule.
 * @param {{feedback?: string, remember?: boolean, rerun?: boolean}} form
 * @returns {string|null} an error message, or null
 */
export function validateRejection({ feedback = '', remember = false, rerun = true } = {}) {
  const text = String(feedback ?? '').trim();
  if (!text && (remember || rerun)) return t('td.reject.required');
  if (text.length > MAX_FEEDBACK) return t('td.reject.tooLong', { max: MAX_FEEDBACK });
  return null;
}

/** Body of POST /api/tasks/:id/reject. */
export function buildRejectBody({ feedback = '', remember = false, rerun = true } = {}) {
  return { feedback: String(feedback ?? '').trim(), remember: !!remember, rerun: rerun !== false };
}

/**
 * Body of POST /api/tasks/:id/approve: editedDraft only when the text really changed.
 * @param {string} original the agent's draft
 * @param {string|null|undefined} current the text in the editor
 */
export function buildApproveBody(original, current) {
  if (current == null) return {};
  const a = String(original ?? '').replace(/\r\n?/g, '\n').trim();
  const b = String(current).replace(/\r\n?/g, '\n').trim();
  return a === b ? {} : { editedDraft: String(current).replace(/\r\n?/g, '\n') };
}

/**
 * Parses a JSON draft: the whole text, or the content of its first ``` fence.
 * @param {string} text
 * @returns {{ok: true, value: any}|{ok: false}}
 */
export function parseJsonDraft(text) {
  const s = String(text ?? '').trim();
  if (!s) return { ok: false };
  const tryParse = (v) => { try { return { ok: true, value: JSON.parse(v) }; } catch { return null; } };
  const whole = tryParse(s);
  if (whole) return whole;
  const fence = /```[a-zA-Z0-9_-]*\n([\s\S]*?)\n?```/.exec(s);
  if (fence) {
    const inner = tryParse(fence[1].trim());
    if (inner) return inner;
  }
  return { ok: false };
}

/** An error message when a JSON draft does not parse, else null. */
export function jsonDraftProblem(text) {
  return parseJsonDraft(text).ok ? null : t('td.approval.jsonInvalid');
}

/**
 * Pretty JSON of a task output (an object, or a string holding JSON). '' when there is nothing.
 * @param {unknown} output
 */
export function prettyJson(output) {
  if (output === undefined || output === null) return '';
  if (typeof output === 'string') {
    const p = parseJsonDraft(output);
    return p.ok ? JSON.stringify(p.value, null, 2) : output;
  }
  try { return JSON.stringify(output, null, 2); } catch { return String(output); }
}

const IMAGE_MIMES = /^image\/(png|jpe?g|gif|webp|avif|svg\+xml)$/i;
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|avif|svg)$/i;

/** True when a task file can be shown as an image thumbnail. */
export function isImageFile(file) {
  if (!file) return false;
  if (typeof file.mime === 'string' && file.mime) return IMAGE_MIMES.test(file.mime);
  return IMAGE_EXT.test(String(file.name || ''));
}

/**
 * Same-origin URL of a task file. The server's `url` is used only when it is a plain path under
 * /api/tasks/; otherwise the URL is built from the ids (each segment encoded).
 * @param {string} taskId
 * @param {{name: string, url?: string}} file
 */
export function fileUrl(taskId, file) {
  const u = typeof file?.url === 'string' ? file.url : '';
  if (/^\/api\/tasks\/[^/?#\\]+\/files\/[^/?#\\]+(\?[^#\\]*)?$/.test(u) && !/(^|\/)(\.|%2e){1,2}(\/|$)/i.test(u)) return u;
  return `/api/tasks/${encodeURIComponent(String(taskId))}/files/${encodeURIComponent(String(file?.name ?? ''))}`;
}

/**
 * URL of an input file of a task (SPEC §16), always rebuilt from its name:
 * /api/tasks/<id>/files/in/<name>.
 * @param {string} taskId
 * @param {{name?: string}} file
 */
export function inputFileUrl(taskId, file) {
  return `/api/tasks/${encodeURIComponent(String(taskId))}/files/in/${encodeURIComponent(String(file?.name ?? ''))}`;
}

const HEAVY = ['input', 'draft', 'result', 'output', 'log'];

/**
 * A task summary built from a detail (or any task-like response), for store.applyTask(): heavy
 * fields are dropped and the summary flags are filled in.
 * @param {object} detail
 * @returns {object|null}
 */
export function summaryFromDetail(detail) {
  if (!detail || typeof detail !== 'object' || typeof detail.id !== 'string' || !detail.state) return null;
  const out = {};
  for (const [k, v] of Object.entries(detail)) if (!HEAVY.includes(k)) out[k] = v;
  if (detail.source && typeof detail.source === 'object') {
    const { payload, ...rest } = detail.source;
    out.source = rest;
  }
  if (out.inputPreview === undefined && typeof detail.input === 'string') out.inputPreview = detail.input.slice(0, 200);
  if (out.hasDraft === undefined) out.hasDraft = !!detail.draft;
  if (out.hasResult === undefined) out.hasResult = !!detail.result;
  if (out.fileCount === undefined && Array.isArray(detail.files)) out.fileCount = detail.files.length;
  if (Array.isArray(out.files)) out.files = out.files.map(({ url, ...f }) => f);
  return out;
}

/**
 * A long text cut for a preview: the first `maxLines` lines and at most `maxChars` characters.
 * @param {string} text
 * @param {{maxChars?: number, maxLines?: number}} [opts]
 * @returns {{text: string, truncated: boolean}}
 */
export function clipText(text, { maxChars = 600, maxLines = 8 } = {}) {
  const s = String(text ?? '');
  let cut = s;
  const lines = s.split('\n');
  if (lines.length > maxLines) cut = lines.slice(0, maxLines).join('\n');
  if (cut.length > maxChars) cut = cut.slice(0, maxChars);
  if (cut.length === s.length) return { text: s, truncated: false };
  return { text: `${cut.trimEnd()}…`, truncated: true };
}

/** Sort key of a waiting task: when it started waiting. */
export function waitingKey(task) {
  return toMs(task?.waitingSince) ?? toMs(task?.updatedAt) ?? toMs(task?.createdAt) ?? 0;
}

/**
 * The approvals queue: every awaiting_approval task the user can operate on, grouped by floor.
 * Tasks are oldest first inside a group; groups are ordered by their oldest task, so the very
 * oldest draft always comes first.
 * @param {Iterable<object>} tasks summaries
 * @param {{floors?: object[], canOperate?: (task: object) => boolean, exclude?: (task: object) => boolean}} [opts]
 * @returns {{groups: Array<{floorId: string, floorName: string, color: string|null, tasks: object[]}>, list: object[]}}
 */
export function approvalQueue(tasks, { floors = [], canOperate = () => true, exclude = () => false } = {}) {
  const byFloor = new Map();
  for (const tk of tasks || []) {
    if (!tk || tk.state !== 'awaiting_approval' || !canOperate(tk) || exclude(tk)) continue;
    const fid = tk.floorId || '';
    if (!byFloor.has(fid)) {
      const f = floors.find((x) => x.id === fid);
      byFloor.set(fid, { floorId: fid, floorName: f?.name || tk.floorName || fid, color: f?.color || null, order: f?.order ?? 0, tasks: [] });
    }
    byFloor.get(fid).tasks.push(tk);
  }
  const groups = [...byFloor.values()];
  for (const g of groups) g.tasks.sort((a, b) => waitingKey(a) - waitingKey(b) || String(a.id).localeCompare(String(b.id)));
  groups.sort((a, b) => waitingKey(a.tasks[0]) - waitingKey(b.tasks[0]) || a.order - b.order);
  return { groups: groups.map(({ order, ...g }) => g), list: groups.flatMap((g) => g.tasks) };
}

/**
 * The id `delta` steps away from `currentId` in `list` (clamped to the ends; first item when the
 * current one is not in the list).
 * @param {Array<{id: string}>} list
 * @param {string|null} currentId
 * @param {number} delta
 */
export function queueStep(list, currentId, delta) {
  if (!list.length) return null;
  const idx = list.findIndex((x) => x.id === currentId);
  if (idx < 0) return list[0].id;
  return list[Math.max(0, Math.min(list.length - 1, idx + delta))].id;
}

/**
 * After `removedId` left the queue, the task to show: the next one of the old order still in the
 * new list, else the previous one, else the first of the new list (null when empty).
 * @param {Array<{id: string}>} oldList
 * @param {Array<{id: string}>} newList
 * @param {string|null} removedId
 */
export function nextAfterRemoval(oldList, newList, removedId) {
  if (!newList.length) return null;
  const ids = new Set(newList.map((x) => x.id));
  const idx = oldList.findIndex((x) => x.id === removedId);
  if (idx >= 0) {
    for (let i = idx + 1; i < oldList.length; i += 1) if (ids.has(oldList[i].id)) return oldList[i].id;
    for (let i = idx - 1; i >= 0; i -= 1) if (ids.has(oldList[i].id)) return oldList[i].id;
  }
  return newList[0].id;
}

/**
 * The status block of the task drawer: what is happening now, in one title and one hint.
 * Returns null when the drawer shows nothing there (done, unless blocked; awaiting approval for someone who can
 * approve — the approval box says it all).
 * @param {object} task
 * @param {{canOperate?: boolean, now?: number, activity?: string}} [opts] activity: "utilise Gmail"
 * @returns {{tone: string, icon: string, title: string, hint: string, live: boolean}|null}
 */
export function statusSummary(task, { canOperate = false, now = Date.now(), activity = '' } = {}) {
  const times = taskTimes(task, now);
  const since = (v) => {
    const ms = toMs(v);
    return ms ? formatDuration(Math.max(0, now - ms)) : '';
  };
  // Ignored by a person, or resolved by a later run of its routine: no error any more.
  if (task?.ignored) {
    const by = typeof task.ignored.byName === 'string' && task.ignored.byName ? task.ignored.byName : '';
    return { tone: 'muted', icon: 'eyeOff', title: by ? t('td.status.ignoredBy', { name: by }) : t('td.status.ignored'),
      hint: task.ignored.fromState === 'awaiting_approval' ? t('td.status.ignoredDraftHint') : t('td.status.ignoredHint'), live: false };
  }
  if (task?.superseded && (task.state === 'failed' || (task.state === 'done' && task.blocked))) {
    return { tone: 'success', icon: 'check', title: t('td.status.superseded'),
      hint: task.superseded.by ? t('td.status.supersededHint') : t('td.status.supersededIdleHint'), live: false };
  }
  switch (task?.state) {
    case 'scheduled':
      return { tone: 'info', icon: 'calendar', title: t('td.status.scheduled', { when: times.due ? relativeTime(times.due, now) : '' }).trim(),
        hint: t('td.status.scheduledHint'), live: false };
    case 'queued': {
      const d = since(task.queuedAt ?? task.updatedAt ?? task.createdAt);
      return { tone: 'neutral', icon: 'clock', title: t('td.status.queued'), hint: d ? t('td.status.queuedHint', { duration: d }) : '', live: false };
    }
    case 'running':
    case 'executing': {
      const parts = [];
      if (times.durationMs !== null) parts.push(t('td.status.elapsed', { duration: formatDuration(times.durationMs) }));
      if (activity) parts.push(activity);
      return { tone: 'running', icon: 'bolt', title: t(task.state === 'executing' ? 'td.status.executing' : 'td.status.running'),
        hint: parts.join(' · '), live: true };
    }
    case 'awaiting_approval':
      return canOperate ? null
        : { tone: 'amber', icon: 'inbox', title: t('td.status.waitingOther'), hint: t('td.status.waitingOtherHint'), live: false };
    case 'done':
      // « À RÉGLER : … » (SPEC-EQUIPE §3): done, but a person must settle something (the floor's
      // « À régler » bubble lists it until someone marks it settled).
      if (task.todo && !task.blocked) {
        return { tone: 'amber', icon: 'alert', title: task.settled ? t('td.status.todoSettled') : t('td.status.todo'),
          hint: typeof task.todo.reason === 'string' && task.todo.reason.trim() ? task.todo.reason.trim() : t('td.status.todoHint'), live: false };
      }
      // « BLOQUÉ : … » (SPEC-EQUIPE §3): an external cause stopped the agent; the reason is the hint.
      if (!task.blocked) return null;
      return { tone: 'amber', icon: 'alert', title: t('td.status.blocked'),
        hint: typeof task.blocked.reason === 'string' && task.blocked.reason.trim() ? task.blocked.reason.trim() : t('td.status.blockedHint'), live: false };
    case 'failed':
      return { tone: 'danger', icon: 'alert', title: t('td.status.failed'),
        hint: typeof task.error === 'string' && task.error.trim() ? task.error.trim().slice(0, 2000) : t('td.status.failedHint'), live: false };
    case 'rejected':
      return { tone: 'muted', icon: 'close', title: t('td.status.rejected'), hint: '', live: false };
    case 'cancelled':
      return { tone: 'muted', icon: 'close', title: t('td.status.cancelled'), hint: '', live: false };
    default:
      return null;
  }
}

/**
 * The rows of the "DÉTAILS" block: timings, engine, autonomy, revisions, format.
 * @param {object} task
 * @param {{now?: number}} [opts]
 * @returns {Array<{key: string, label: string, value: string}>}
 */
export function detailRows(task, { now = Date.now() } = {}) {
  const times = taskTimes(task, now);
  const rows = [];
  const add = (key, labelKey, value) => { if (value) rows.push({ key, label: t(labelKey), value }); };
  add('created', 'td.time.created', times.created ? formatDateTime(times.created, now) : '');
  if (task?.state === 'scheduled' || times.due) add('due', 'td.time.due', times.due ? formatDateTime(times.due, now) : '');
  add('started', 'td.time.started', times.started ? formatDateTime(times.started, now) : '');
  if (task?.state === 'awaiting_approval') add('waiting', 'td.time.waiting', times.waitingSince ? relativeTime(times.waitingSince, now) : '');
  add('finished', 'td.time.finished', times.finished && FINAL_STATES.has(task?.state) ? formatDateTime(times.finished, now) : '');
  add('duration', 'td.time.duration', times.durationMs !== null ? formatDuration(times.durationMs) : '');
  const engine = typeof task?.engine === 'string' && task.engine ? task.engine : '';
  if (engine || task?.model) {
    const name = engine ? t(`engine.name.${engine}`) : '';
    const bits = [name && name !== `engine.name.${engine}` ? name : engine, task?.model ? String(task.model) : '', task?.effort ? String(task.effort) : ''].filter(Boolean);
    add('engine', 'td.engine', bits.join(' · '));
  }
  add('autonomy', 'td.autonomy', task?.autonomy === 'auto' ? t('td.autonomyAuto') : task?.autonomy === 'approve' ? t('td.autonomyApprove') : '');
  if (Number(task?.revisions) > 0) add('revisions', 'td.revisions', String(Number(task.revisions)));
  if (task?.outputFormat === 'json') add('format', 'td.format', t('td.format.json'));
  return rows;
}

/**
 * The approval decisions of a task, newest last, as display lines.
 * @param {Array<{byName?: string, at?: any, decision: string, feedback?: string, edited?: boolean}>} approvals
 * @returns {Array<{decision: string, text: string, at: number|null, feedback: string}>}
 */
export function decisionLines(approvals) {
  const out = [];
  for (const a of Array.isArray(approvals) ? approvals : []) {
    if (!a || (a.decision !== 'approved' && a.decision !== 'rejected')) continue;
    const name = typeof a.byName === 'string' && a.byName.trim() ? a.byName.trim() : t('td.decision.someone');
    const key = a.decision === 'rejected' ? 'td.decision.rejected' : a.edited ? 'td.decision.approvedEdited' : 'td.decision.approved';
    out.push({ decision: a.decision, text: t(key, { name }), at: toMs(a.at), feedback: typeof a.feedback === 'string' ? a.feedback : '' });
  }
  return out;
}

/**
 * True when a task event (summary) means the drawer's detail is out of date.
 * @param {object|null} detail what the drawer shows
 * @param {object|null} summary the event
 */
export function needsReload(detail, summary) {
  if (!summary || typeof summary !== 'object') return false;
  if (!detail) return true;
  if (summary.state !== detail.state) return true;
  const a = toMs(summary.updatedAt);
  const b = toMs(detail.updatedAt);
  if (a === null || b === null) return true;
  return a > b;
}

/**
 * Which deliverable the drawer shows: the result when there is one, else the draft (read-only
 * unless the approval box shows it).
 * @param {object} task detail
 * @returns {{kind: 'result'|'draft'|null, text: string}}
 */
export function deliverableOf(task) {
  if (typeof task?.result === 'string' && task.result.trim()) return { kind: 'result', text: task.result };
  if (typeof task?.draft === 'string' && task.draft.trim()) return { kind: 'draft', text: task.draft };
  return { kind: null, text: '' };
}

/**
 * Pretty JSON of a trigger payload, capped for display.
 * @param {unknown} payload
 * @param {number} [max]
 */
export function payloadPreview(payload, max = 20000) {
  if (payload === undefined || payload === null) return '';
  let s;
  try { s = typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2); } catch { s = String(payload); }
  if (typeof s !== 'string') return '';
  return s.length > max ? `${s.slice(0, max)}\n…` : s;
}

/**
 * The display name of a routine or trigger from a GET /api/routines/:id or /api/triggers/:id
 * answer (the object itself, or wrapped as {routine} / {trigger}). '' when unreadable.
 * @param {unknown} res
 * @param {'routine'|'trigger'} kind
 */
export function sourceNameOf(res, kind) {
  const item = res && typeof res === 'object' && res[kind] && typeof res[kind] === 'object' ? res[kind] : res;
  if (!item || typeof item !== 'object') return '';
  const name = kind === 'routine' ? (item.title ?? item.name) : (item.name ?? item.title);
  return typeof name === 'string' ? name.trim().slice(0, 120) : '';
}
